/**
 * What an apartment can sell alongside the stay itself, along the two axes that actually decide
 * behaviour. Pure, and deliberately apart from `api.stayExtension` so it carries no HTTP client
 * with it — every screen in the feature branches on these, including ones with no network at all.
 *
 * <p><b>Never branch on a constant to ask "which end is this".</b> Before luggage existed the
 * question was written everywhere as `type === 'EARLY_CHECKIN' ? … : …`, and each of those `else`
 * branches meant "late check-out". Adding a type makes them all wrong at once, silently: a bag
 * drop-off renders, and charges, as a late check-out. Ask {@link isArrival} instead.
 */
export type StayExtensionType =
  | 'EARLY_CHECKIN' | 'LATE_CHECKOUT' | 'LUGGAGE_DROPOFF' | 'LUGGAGE_PICKUP';

/**
 * Which end of the stay this hangs off — and therefore which date it uses, which standard hour it
 * is measured against, and which way its price stair runs.
 */
export const isArrival = (type: StayExtensionType) =>
  type === 'EARLY_CHECKIN' || type === 'LUGGAGE_DROPOFF';

/**
 * True for the products that store bags rather than occupying the apartment.
 *
 * <p>The distinction decides three things: whether granting moves the stay's hours and reissues
 * the door code, whether the cleaning-window and contention rules apply at all, and which of the
 * apartment's on/off switches governs it.
 */
export const isLuggage = (type: StayExtensionType) => type.startsWith('LUGGAGE');
